import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, playlistApi } from '../utils/api';
import { getPieceData } from '../components/ChessDiagram';
import {
  buildBoardSvg,
  parseFen,
  fetchPieceSet,
  DEFAULT_LIGHT_SQUARE,
  DEFAULT_DARK_SQUARE,
  DEFAULT_BORDER_COLOR,
} from './ChessDiagramGenerator';

// ── PDF helpers (mirrored from QuizReportPage) ────────────────────────────────

function svgToDataUrl(svgString) {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
}

function svgToPng(svgString) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth  || 280;
      const h = img.naturalHeight || 260;
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width  = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ png: canvas.toDataURL('image/png'), w, h });
    };
    img.onerror = reject;
    img.src = svgToDataUrl(svgString);
  });
}

function renderHeaderPng(subtitle) {
  const W = 500, H = subtitle ? 72 : 52, scale = 3;
  const canvas = document.createElement('canvas');
  canvas.width  = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.clearRect(0, 0, W, H);

  const brandColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--theme-brand-nav').trim() || '#1c3a5c';

  // Main title
  ctx.font = 'bold 36px serif';
  ctx.fillStyle = brandColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('♚  Chess Flashcards', W / 2, subtitle ? 22 : H / 2);

  // Subtitle (playlist title)
  if (subtitle) {
    ctx.font = '18px sans-serif';
    ctx.fillStyle = '#555';
    ctx.fillText(subtitle, W / 2, 52);
  }

  return canvas.toDataURL('image/png');
}

async function savePdf(cards, prefs, layout, filename, subtitle) {
  const { jsPDF } = await import('jspdf');

  const perPage   = Number(layout);
  const cols      = 2;
  const rowsPerPg = perPage / cols;

  const pageW     = 210;
  const pageH     = 297;
  const margin    = 10;
  const topMargin = 1;
  const headerH   = subtitle ? 20 : 16;
  const gapX      = 5;
  const gapY      = 5;

  const cellW = (pageW - 2 * margin - (cols - 1) * gapX) / cols;
  const cellH = (pageH - topMargin - margin - headerH - (rowsPerPg - 1) * gapY) / rowsPerPg;
  const diagW = ({ 4: cellW - 4, 6: 55, 8: 45, 10: 38 })[perPage] ?? (cellW - 4);

  const headerPng  = renderHeaderPng(subtitle);
  const headerImgW = 80;
  const headerImgH = headerImgW * ((subtitle ? 72 : 52) / 500);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const setKey = prefs.diagramPieceSet ?? 'cburnett';
  let pieceData = getPieceData(setKey);
  if (!pieceData) {
    try   { pieceData = await fetchPieceSet(setKey); }
    catch  { pieceData = null; }
  }

  for (let i = 0; i < cards.length; i++) {
    const col     = i % cols;
    const rowOnPg = Math.floor(i / cols) % rowsPerPg;

    if (i > 0 && col === 0 && rowOnPg === 0) doc.addPage();

    const cellX = margin + col * (cellW + gapX);
    const cellY = topMargin + headerH + rowOnPg * (cellH + gapY);

    doc.setDrawColor(180);
    doc.setLineWidth(0.3);
    doc.rect(cellX, cellY, cellW, cellH);

    const card     = cards[i];
    const flipped  = card.flipped ?? prefs.diagramFlipped ?? false;
    const light    = prefs.diagramLightSquare    ?? DEFAULT_LIGHT_SQUARE;
    const dark     = prefs.diagramDarkSquare     ?? DEFAULT_DARK_SQUARE;
    const border   = prefs.diagramBorderColor    ?? DEFAULT_BORDER_COLOR;
    const coordPos = prefs.diagramCoordPosition  ?? 'border';
    const tight    = perPage === 10;

    let curY = cellY + (tight ? 3 : 4);

    // Card name
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const nameLines = doc.splitTextToSize(card.name, cellW - 4);
    doc.text(nameLines, cellX + 2, curY);
    curY += nameLines.length * 3.8 + (tight ? 0 : 1);

    // Chess diagram
    if (card.fen) {
      try {
        const { board, activeColor } = parseFen(card.fen);
        const svgString = buildBoardSvg({
          board, activeColor, flipped,
          setKey, pieceData,
          boardSize:     240,
          lightSquare:   light,
          darkSquare:    dark,
          borderColor:   border,
          coordPosition: coordPos,
        });
        const { png, w, h } = await svgToPng(svgString);
        const diagH = diagW * (h / w);
        const imgX  = cellX + (cellW - diagW) / 2;
        doc.addImage(png, 'PNG', imgX, curY, diagW, diagH);
        curY += diagH + (tight ? 1 : 2);
      } catch (e) {
        console.warn('PDF: diagram error for card', card.id, e);
      }
    }

    // Problem
    if (card.problem) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.text('Problem:', cellX + 2, curY);
      curY += 3.2;
      doc.setFont('helvetica', 'normal');
      const remaining    = cellY + cellH - curY - 3;
      const maxProbLines = Math.max(1, Math.floor(remaining / 3));
      const probLines    = doc.splitTextToSize(card.problem, cellW - 4);
      doc.text(probLines.slice(0, maxProbLines), cellX + 2, curY);
    }
  }

  // Stamp header + page number on every page
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const imgX  = (pageW - headerImgW) / 2;
    const imgY  = topMargin + (headerH - headerImgH) / 2 - 1;
    doc.addImage(headerPng, 'PNG', imgX, imgY, headerImgW, headerImgH);

    const footerY = topMargin + headerH + rowsPerPg * cellH + (rowsPerPg - 1) * gapY + 4;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text('www.chessflashcards.org', margin, footerY);
    doc.text(`Page ${p}`, pageW - margin, footerY, { align: 'right' });
    doc.setTextColor(0);
  }

  doc.save(filename);
}

// ── Fisher-Yates shuffle (returns new array) ──────────────────────────────────

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PlaylistExportPage() {
  const { id }           = useParams();
  const { token, prefs } = useAuth();
  const navigate         = useNavigate();

  const [playlist, setPlaylist]   = useState(null);
  const [cards, setCards]         = useState([]);      // ordered flashcard objects
  const [layout, setLayout]       = useState('4');
  const [randomOrder, setRandomOrder] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pl    = await playlistApi.get(id, token);
      setPlaylist(pl);

      const items = (pl.items || []).slice().sort((a, b) => a.sort_order - b.sort_order);
      const fetched = await Promise.all(
        items.map(item => flashcardApi.get(item.flashcard_id, token).catch(() => null))
      );
      setCards(fetched.filter(Boolean));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    if (cards.length === 0) { alert('This playlist has no cards.'); return; }
    setExporting(true);
    try {
      const exportCards = randomOrder ? shuffle(cards) : cards;
      const safeTitle   = (playlist?.title || 'playlist')
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      await savePdf(
        exportCards,
        prefs,
        layout,
        `playlist-${safeTitle}.pdf`,
        playlist?.title || '',
      );
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  return (
    <>
      {/* Page title */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Playlist {playlist?.title} Export</h2>
        <button className="btn btn-link" onClick={() => navigate('/playlists')}>
          ← Back to Playlists
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Options row */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body d-flex flex-wrap gap-4 align-items-center">
          {/* Layout picker */}
          <div>
            <label className="form-label fw-semibold mb-1">Layout</label>
            <div className="d-flex gap-2">
              {['4', '6', '8', '10'].map(n => (
                <button
                  key={n}
                  className={`btn btn-sm ${layout === n ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setLayout(n)}
                >
                  {n}-up
                </button>
              ))}
            </div>
          </div>

          {/* Random order checkbox — vertically aligned with the buttons */}
          <div className="form-check mb-0" style={{ marginTop: '1.6rem' }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="chk-random"
              checked={randomOrder}
              onChange={e => setRandomOrder(e.target.checked)}
            />
            <label className="form-check-label fw-semibold" htmlFor="chk-random">
              Random order?
            </label>
          </div>
        </div>
      </div>

      {/* Card list — up to 3 columns, numbers instead of checkboxes */}
      {cards.length === 0 ? (
        <div className="text-muted mb-4">This playlist has no cards yet.</div>
      ) : (
        <>
          <div className="small text-muted mb-2">
            {cards.length} card{cards.length !== 1 ? 's' : ''}
            {randomOrder && ' · order will be randomized on export'}
          </div>
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2 mb-4">
            {cards.map((card, index) => (
              <div className="col" key={card.id}>
                <div className="card h-100">
                  <div className="card-body py-2 d-flex align-items-start gap-2">
                    <span
                      className="text-muted fw-bold flex-shrink-0"
                      style={{ minWidth: '1.75rem', fontSize: '0.85rem', paddingTop: 1 }}
                    >
                      {index + 1}.
                    </span>
                    <div>
                      <div className="fw-semibold small">{card.name}</div>
                      {card.problem && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {card.problem.slice(0, 80)}{card.problem.length > 80 ? '…' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="d-flex gap-2 align-items-center">
        <button
          className="btn btn-primary btn-lg"
          onClick={handleExport}
          disabled={exporting || cards.length === 0}
        >
          {exporting ? 'Generating PDF…' : 'Export'}
        </button>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/playlists')}>
          Cancel
        </button>
      </div>
    </>
  );
}
